import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const BOARD_MODEL_URL = "/design-preview/mirror-board.glb";
const BLOCK_SIZE = 1.94;
const TAP_MAX_DISTANCE_PX = 7;
const TAP_MAX_DURATION_MS = 650;

// Each lobby block covers 2x2 squares of the real XIMA board (13x13 squares,
// floors at y 0.5 / 2.5 / 4.5 and a one-square tower at the center).
// [x, z] is the block center; blocks rest on the tallest square below them and
// keep at least one free square between each other.
export const LOBBY_BOARD_LAYOUT = {
  // Floor 4: centered on the top of the pyramid.
  play: [0, 0],
  // Floor 3.
  puzzles: [-1.5, 1.5],
  learn: [1.5, 1.5],
  history: [-1.5, -1.5],
  // Floor 2: two in front, one on each side, one behind.
  create: [-2.5, 3.5],
  find: [1.5, 3.5],
  bot: [-3.5, -0.5],
  watch: [3.5, -0.5],
  local: [-0.5, -3.5],
  // Floor 1: two in front, one on each side, two behind.
  science: [-3.5, 5.5],
  tournaments: [2.5, 5.5],
  cienzar: [-5.5, -0.5],
  contrazar: [5.5, -0.5],
  chance: [-3.5, -5.5],
  sandbox: [2.5, -5.5],
};

export const LOBBY_BOARD_ICON_PATHS = {
  play: '<circle cx="12" cy="12" r="9"/><path d="M10 8.5l5 3.5-5 3.5z"/>',
  trophy:
    '<path d="M7 4h10v4a5 5 0 0 1-10 0z"/><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4M9 20h6M12 13v7"/>',
  plus: '<circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/>',
  list: '<path d="M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2"/>',
  people:
    '<circle cx="8" cy="9" r="3"/><circle cx="16" cy="9" r="3"/><path d="M3 19c.8-3 2.8-4.5 5-4.5s4.2 1.5 5 4.5M11 19c.8-3 2.8-4.5 5-4.5s4.2 1.5 5 4.5"/>',
  controller: '<rect x="3" y="8" width="18" height="10" rx="5"/><path d="M8 11v4M6 13h4M15 12h.01M17 14h.01"/>',
  folder: '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
  binoculars:
    '<path d="M9.5 6.5V5a2 2 0 0 0-4 0v1.5M18.5 6.5V5a2 2 0 0 0-4 0v1.5"/><path d="M3 15.5l2.2-9h4.6l1 4h2.4l1-4h4.6l2.2 9"/><circle cx="6.5" cy="16" r="4"/><circle cx="17.5" cy="16" r="4"/><circle cx="6.5" cy="16" r="1.6"/><circle cx="17.5" cy="16" r="1.6"/>',
  cube: '<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/>',
  puzzle:
    '<path d="M9 4h3a2 2 0 1 1 4 0h2v5a2 2 0 1 0 0 4v5h-5a2 2 0 1 0-4 0H4v-5a2 2 0 1 1 0-4V4z"/>',
  book: '<path d="M3 5h6a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H3zM21 5h-6a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h7z"/>',
  // Podio de premiación: 2.º, 1.º y 3.er puesto.
  podium:
    '<path d="M9 21V10h6v11M3 21v-7h6M15 21v-5h6v5M2 21h20"/><path d="M12 3l.9 1.8 2 .3-1.45 1.4.35 2-1.8-.95-1.8.95.35-2L9.1 5.1l2-.3z"/>',
  stats: '<path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/>',
  globe:
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z"/>',
  gear:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>',
  cart: '<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.7 12.4a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.6L22 7H6"/>',
  friends:
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.8-3.6 3.3-5.5 6.5-5.5s5.7 1.9 6.5 5.5"/><path d="M18.5 12.5l-.6-.55C16.2 10.4 15 9.3 15 8a2 2 0 0 1 3.5-1.3A2 2 0 0 1 22 8c0 1.3-1.2 2.4-2.9 3.95z"/>',
  brush:
    '<path d="M14.5 3.5l6 6-9.5 9.5H5v-6z"/><path d="M12.5 5.5l6 6M5 19c0 1.5-1 2.5-2.5 2.5"/>',
  headset:
    '<path d="M4 14v-2a8 8 0 0 1 16 0v2"/><rect x="2.5" y="14" width="4" height="6" rx="1.5"/><rect x="17.5" y="14" width="4" height="6" rx="1.5"/><path d="M19.5 20c0 1.4-1.6 2-4 2h-2"/>',
};

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

const loadBlockIcon = (block) =>
  block.image
    ? loadImage(block.image)
    : loadImage(
        `data:image/svg+xml;utf8,${encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="256" height="256" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${
            LOBBY_BOARD_ICON_PATHS[block.iconKey] || ""
          }</svg>`,
        )}`,
      );

const darkenColor = (hex, amount) => {
  const value = parseInt(String(hex).replace("#", ""), 16);
  const channel = (shift) => Math.round(((value >> shift) & 255) * (1 - amount));
  return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
};

// Faces are a gradient of the block color, or a soft multicolor design when the
// block defines a palette ("quadrants": one color per corner, "bands": diagonal stripes).
const paintFaceBackground = (context, size, block) => {
  const palette = Array.isArray(block.palette) ? block.palette.filter(Boolean) : [];
  if (palette.length >= 2 && block.facePattern === "bands") {
    const bandWidth = (size * 2) / palette.length;
    context.save();
    context.translate(size / 2, size / 2);
    context.rotate(-Math.PI / 4);
    palette.forEach((color, index) => {
      const band = context.createLinearGradient(0, -size + index * bandWidth, 0, -size + (index + 1) * bandWidth);
      band.addColorStop(0, color);
      band.addColorStop(1, palette[(index + 1) % palette.length]);
      context.fillStyle = band;
      context.fillRect(-size, -size + index * bandWidth, size * 2, bandWidth + 1);
    });
    context.restore();
  } else if (palette.length >= 2) {
    context.fillStyle = "#f4f0ff";
    context.fillRect(0, 0, size, size);
    const corners = [
      [0, 0],
      [size, 0],
      [size, size],
      [0, size],
    ];
    palette.slice(0, 4).forEach((color, index) => {
      const [x, y] = corners[index];
      const glow = context.createRadialGradient(x, y, 0, x, y, size * 0.95);
      glow.addColorStop(0, color);
      glow.addColorStop(1, "rgba(255,255,255,0)");
      context.fillStyle = glow;
      context.fillRect(0, 0, size, size);
    });
  } else {
    const fill = context.createLinearGradient(0, 0, size, size);
    fill.addColorStop(0, block.color);
    fill.addColorStop(1, darkenColor(block.color, 0.45));
    context.fillStyle = fill;
    context.fillRect(0, 0, size, size);
    return;
  }
  // Soft veil keeps the white icon and name readable over the colors.
  context.fillStyle = "rgba(20, 12, 40, 0.22)";
  context.fillRect(0, 0, size, size);
};

const createFaceTexture = (block, icon, withLabel, fontFamily) => {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  paintFaceBackground(context, size, block);
  const shine = context.createRadialGradient(size * 0.3, size * 0.2, 10, size * 0.3, size * 0.2, size * 0.8);
  shine.addColorStop(0, "rgba(255,255,255,0.32)");
  shine.addColorStop(1, "rgba(255,255,255,0)");
  context.fillStyle = shine;
  context.fillRect(0, 0, size, size);
  context.strokeStyle = "rgba(255,255,255,0.8)";
  context.lineWidth = 14;
  context.strokeRect(10, 10, size - 20, size - 20);

  const iconSize = withLabel ? 220 : 280;
  if (icon) {
    context.shadowColor = "rgba(0,0,0,0.5)";
    context.shadowBlur = 14;
    context.drawImage(icon, (size - iconSize) / 2, withLabel ? 60 : (size - iconSize) / 2, iconSize, iconSize);
    context.shadowBlur = 0;
  }
  if (withLabel) {
    context.fillStyle = "#ffffff";
    context.shadowColor = "rgba(0,0,0,0.7)";
    context.shadowBlur = 8;
    context.textAlign = "center";
    const fontSize = 62;
    context.font = `800 ${fontSize}px ${fontFamily}`;
    const lines = [];
    let current = "";
    String(block.label || "")
      .toUpperCase()
      .split(" ")
      .forEach((word) => {
        const candidate = current ? `${current} ${word}` : word;
        if (context.measureText(candidate).width > size - 60 && current) {
          lines.push(current);
          current = word;
        } else {
          current = candidate;
        }
      });
    lines.push(current);
    lines.slice(0, 2).forEach((line, index) => {
      const y = 345 + fontSize + index * (fontSize + 6) - (Math.min(lines.length, 2) - 1) * (fontSize / 2);
      context.fillText(line, size / 2, y, size - 40);
    });
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
};

const createBoardMaterial = (materialName) => {
  const name = String(materialName || "").toLowerCase();
  if (name.includes("casillas oscuras")) {
    return new THREE.MeshPhysicalMaterial({
      color: "#005c4a",
      metalness: 0.86,
      roughness: 0.08,
      clearcoat: 1,
      clearcoatRoughness: 0.04,
      side: THREE.DoubleSide,
    });
  }
  if (name.includes("casillas claras")) {
    return new THREE.MeshStandardMaterial({ color: "#fff7f7", metalness: 0.03, roughness: 0.62, side: THREE.DoubleSide });
  }
  if (name.includes("paredes")) {
    return new THREE.MeshPhysicalMaterial({
      color: 0xb9eff7,
      metalness: 0.02,
      roughness: 0.08,
      transmission: 0.76,
      transparent: true,
      opacity: 0.32,
      thickness: 0.3,
      ior: 1.45,
      side: THREE.DoubleSide,
    });
  }
  return new THREE.MeshPhysicalMaterial({
    color: 0x77c9ed,
    metalness: 0.72,
    roughness: 0.1,
    clearcoat: 1,
    clearcoatRoughness: 0.05,
    side: THREE.DoubleSide,
  });
};

const HIDDEN_SQUARE_COLOR = "#141414";

// Squares under the upper floors show through the glass walls. Only the open
// surface where blocks can rest keeps the chess pattern; covered squares move
// to a separate near-black mesh.
const darkenHiddenSquares = (board, heightAt, ownedMaterials) => {
  const topHeights = new Map();
  for (let z = -6; z <= 6; z += 1) {
    for (let x = -6; x <= 6; x += 1) topHeights.set(`${x},${z}`, heightAt(x, z));
  }
  const squareMeshes = [];
  board.traverse((child) => {
    if (child.isMesh && child.userData.isSquare) squareMeshes.push(child);
  });
  const hiddenMaterial = new THREE.MeshStandardMaterial({
    color: HIDDEN_SQUARE_COLOR,
    metalness: 0.1,
    roughness: 0.85,
    side: THREE.DoubleSide,
  });
  ownedMaterials.push(hiddenMaterial);
  const center = new THREE.Vector3();
  const corner = new THREE.Vector3();
  squareMeshes.forEach((child) => {
    const source = child.geometry;
    const flat = source.index ? source.toNonIndexed() : source.clone();
    const positions = flat.attributes.position;
    const attributeNames = Object.keys(flat.attributes);
    const visible = Object.fromEntries(attributeNames.map((name) => [name, []]));
    const hidden = Object.fromEntries(attributeNames.map((name) => [name, []]));
    for (let vertex = 0; vertex < positions.count; vertex += 3) {
      center.set(0, 0, 0);
      for (let offset = 0; offset < 3; offset += 1) {
        corner.fromBufferAttribute(positions, vertex + offset).applyMatrix4(child.matrixWorld);
        center.add(corner);
      }
      center.divideScalar(3);
      const top = topHeights.get(`${Math.round(center.x)},${Math.round(center.z)}`);
      const target = top === undefined || center.y >= top - 0.05 ? visible : hidden;
      attributeNames.forEach((name) => {
        const attribute = flat.attributes[name];
        for (let offset = 0; offset < 3; offset += 1) {
          for (let item = 0; item < attribute.itemSize; item += 1) {
            target[name].push(attribute.array[(vertex + offset) * attribute.itemSize + item]);
          }
        }
      });
    }
    const build = (data) => {
      const geometry = new THREE.BufferGeometry();
      attributeNames.forEach((name) => {
        geometry.setAttribute(name, new THREE.Float32BufferAttribute(data[name], flat.attributes[name].itemSize));
      });
      return geometry;
    };
    child.geometry = build(visible);
    if (hidden.position.length) {
      const hiddenMesh = new THREE.Mesh(build(hidden), hiddenMaterial);
      hiddenMesh.name = `${child.name} tapadas`;
      hiddenMesh.position.copy(child.position);
      hiddenMesh.quaternion.copy(child.quaternion);
      hiddenMesh.scale.copy(child.scale);
      hiddenMesh.receiveShadow = true;
      child.parent.add(hiddenMesh);
    }
    flat.dispose();
    source.dispose();
  });
  board.updateMatrixWorld(true);
};

const LobbyBoard3D = ({ blocks, className = "", onReady, onUnavailable }) => {
  const mountRef = useRef(null);
  const blocksRef = useRef(blocks);
  const callbacksRef = useRef({ onReady, onUnavailable });
  blocksRef.current = blocks;
  callbacksRef.current = { onReady, onUnavailable };
  const blocksKey = blocks.map((block) => `${block.id}:${block.label}:${block.color}`).join("|");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (error) {
      callbacksRef.current.onUnavailable?.();
      return undefined;
    }

    let disposed = false;
    let boardRoot = null;
    const ownedTextures = [];
    const ownedMaterials = [];
    const ownedGeometries = [];
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.className = "xima-lobby-board3d-canvas";
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 200);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.PAN,
    };
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.minDistance = 10;
    controls.maxDistance = 48;
    controls.maxPolarAngle = Math.PI * 0.47;
    controls.target.set(0, 3.4, 0);
    // Spin slowly like a top while nobody touches the board.
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    controls.autoRotate = !prefersReducedMotion;
    controls.autoRotateSpeed = -1.1;
    let autoRotateTimer = null;
    const handleControlsStart = () => {
      window.clearTimeout(autoRotateTimer);
      controls.autoRotate = false;
    };
    const handleControlsEnd = () => {
      window.clearTimeout(autoRotateTimer);
      if (prefersReducedMotion) return;
      autoRotateTimer = window.setTimeout(() => {
        controls.autoRotate = true;
      }, 2500);
    };
    controls.addEventListener("start", handleControlsStart);
    controls.addEventListener("end", handleControlsEnd);

    scene.add(new THREE.HemisphereLight(0xfff1dd, 0x2a1a0c, 1.5));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.4);
    keyLight.position.set(9, 20, 13);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    Object.assign(keyLight.shadow.camera, { left: -14, right: 14, top: 14, bottom: -14 });
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0xffc27a, 0.9);
    rimLight.position.set(-12, 8, -10);
    scene.add(rimLight);

    const resize = () => {
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      const aspect = width / height;
      const wasPlaced = camera.userData.placed;
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
      if (!wasPlaced) {
        // Narrow (phone) frames need the camera further back to fit the whole board.
        // Default view is 50% closer than the original framing; phones stay wide enough to show the whole board.
        const distance = aspect >= 1.4 ? 20 : aspect >= 1 ? 23 : 28;
        const direction = new THREE.Vector3(0.18, 0.62, 1).normalize();
        camera.position.copy(controls.target).addScaledVector(direction, distance);
        camera.userData.placed = true;
        controls.update();
      }
    };
    resize();
    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(mount);

    const cubes = [];
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered = null;
    let pointerStart = null;

    const pickBlock = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
      return raycaster.intersectObjects(cubes, false)[0]?.object || null;
    };
    const handleMouseDown = (event) => {
      // Stop the browser auto-scroll so the middle wheel can move the board.
      if (event.button === 1) event.preventDefault();
    };
    const handlePointerDown = (event) => {
      pointerStart = event.button === 0 ? { x: event.clientX, y: event.clientY, time: performance.now() } : null;
    };
    const handlePointerUp = (event) => {
      if (!pointerStart) return;
      const moved = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y);
      const elapsed = performance.now() - pointerStart.time;
      pointerStart = null;
      if (moved > TAP_MAX_DISTANCE_PX || elapsed > TAP_MAX_DURATION_MS) return;
      const cube = pickBlock(event);
      if (!cube) return;
      const block = blocksRef.current.find((entry) => entry.id === cube.userData.blockId);
      block?.onClick?.();
    };
    const handlePointerMove = (event) => {
      if (event.pointerType !== "mouse") return;
      hovered = pickBlock(event);
      renderer.domElement.style.cursor = hovered ? "pointer" : "grab";
    };
    const handleContextMenu = (event) => event.preventDefault();
    const handlePointerLeave = () => {
      hovered = null;
    };
    // Phones in portrait: once the board cannot tilt any further, the same
    // vertical drag scrolls the page so the buttons below stay reachable.
    const portraitPhoneQuery = window.matchMedia?.("(max-width: 860px) and (orientation: portrait)");
    const findScrollContainer = () => {
      let element = mount.parentElement;
      while (element && element !== document.body) {
        const overflowY = window.getComputedStyle(element).overflowY;
        if ((overflowY === "auto" || overflowY === "scroll") && element.scrollHeight > element.clientHeight) {
          return element;
        }
        element = element.parentElement;
      }
      return null;
    };
    let lastTouch = null;
    const handleTouchStart = (event) => {
      lastTouch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    };
    const handleTouchMove = (event) => {
      if (!lastTouch || event.touches.length !== 1 || !portraitPhoneQuery?.matches) {
        lastTouch = null;
        return;
      }
      const touch = event.touches[0];
      const deltaX = touch.clientX - lastTouch.x;
      const deltaY = touch.clientY - lastTouch.y;
      lastTouch = { x: touch.clientX, y: touch.clientY };
      if (Math.abs(deltaY) <= Math.abs(deltaX)) return;
      const polar = controls.getPolarAngle();
      const tiltedToBottom = polar >= controls.maxPolarAngle - 0.03 && deltaY < 0;
      const tiltedToTop = polar <= controls.minPolarAngle + 0.03 && deltaY > 0;
      if (!tiltedToBottom && !tiltedToTop) return;
      const container = findScrollContainer();
      if (container) container.scrollBy(0, -deltaY);
      else window.scrollBy(0, -deltaY);
    };
    renderer.domElement.addEventListener("touchstart", handleTouchStart, { passive: true });
    renderer.domElement.addEventListener("touchmove", handleTouchMove, { passive: true });
    renderer.domElement.addEventListener("mousedown", handleMouseDown);
    renderer.domElement.addEventListener("contextmenu", handleContextMenu);
    renderer.domElement.addEventListener("pointerdown", handlePointerDown);
    renderer.domElement.addEventListener("pointerup", handlePointerUp);
    renderer.domElement.addEventListener("pointermove", handlePointerMove);
    renderer.domElement.addEventListener("pointerleave", handlePointerLeave);

    let visible = true;
    const intersectionObserver =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => {
            visible = entries.some((entry) => entry.isIntersecting);
          })
        : null;
    intersectionObserver?.observe(mount);

    const normalScale = new THREE.Vector3(1, 1, 1);
    const hoverScale = new THREE.Vector3(1.06, 1.06, 1.06);
    const panLimit = new THREE.Box3(new THREE.Vector3(-7, -1, -7), new THREE.Vector3(7, 9, 7));
    const clampedTarget = new THREE.Vector3();
    renderer.setAnimationLoop(() => {
      if (!visible) return;
      controls.update();
      // Keep the board on screen while panning.
      clampedTarget.copy(controls.target).clamp(panLimit.min, panLimit.max);
      if (!clampedTarget.equals(controls.target)) {
        camera.position.add(clampedTarget.clone().sub(controls.target));
        controls.target.copy(clampedTarget);
      }
      cubes.forEach((cube) => cube.scale.lerp(cube === hovered ? hoverScale : normalScale, 0.2));
      renderer.render(scene, camera);
    });

    const build = async () => {
      let boardGltf;
      try {
        boardGltf = await new GLTFLoader().loadAsync(BOARD_MODEL_URL);
      } catch (error) {
        boardGltf = null;
      }
      if (disposed) return;
      if (!boardGltf?.scene) {
        callbacksRef.current.onUnavailable?.();
        return;
      }

      const board = boardGltf.scene;
      // Same orientation fix as the design preview: the GLB stores the board in XY.
      board.rotation.x = Math.PI / 2;
      board.traverse((child) => {
        if (!child.isMesh) return;
        child.castShadow = true;
        child.receiveShadow = true;
        const material = createBoardMaterial(child.material?.name);
        child.userData.isSquare = String(child.material?.name || "").toLowerCase().includes("casillas");
        ownedMaterials.push(material);
        ownedGeometries.push(child.geometry);
        child.material = material;
      });
      board.updateMatrixWorld(true);
      scene.add(board);
      boardRoot = board;

      const down = new THREE.Vector3(0, -1, 0);
      const heightAt = (x, z) => {
        raycaster.set(new THREE.Vector3(x, 50, z), down);
        return raycaster.intersectObject(board, true)[0]?.point.y ?? 0;
      };
      darkenHiddenSquares(board, heightAt, ownedMaterials);

      const fontFamily = window.getComputedStyle(document.body).fontFamily || "sans-serif";
      const geometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_SIZE);
      ownedGeometries.push(geometry);
      const icons = await Promise.all(blocksRef.current.map(loadBlockIcon));
      if (disposed) return;

      blocksRef.current.forEach((block, index) => {
        const placement = LOBBY_BOARD_LAYOUT[block.layoutKey];
        if (!placement) return;
        const [x, z] = placement;
        const topTexture = createFaceTexture(block, icons[index], false, fontFamily);
        const frontTexture = createFaceTexture(block, icons[index], true, fontFamily);
        ownedTextures.push(topTexture, frontTexture);
        // BoxGeometry face order: +x, -x, +y, -y, +z, -z. The name goes on the four sides.
        const materials = [frontTexture, frontTexture, topTexture, topTexture, frontTexture, frontTexture].map(
          (map) => new THREE.MeshStandardMaterial({ map, roughness: 0.35, metalness: 0.08 }),
        );
        ownedMaterials.push(...materials);
        const cube = new THREE.Mesh(geometry, materials);
        const base = Math.max(
          heightAt(x - 0.45, z - 0.45),
          heightAt(x + 0.45, z - 0.45),
          heightAt(x - 0.45, z + 0.45),
          heightAt(x + 0.45, z + 0.45),
          heightAt(x, z),
        );
        cube.position.set(x, base + BLOCK_SIZE / 2 + 0.02, z);
        cube.castShadow = true;
        cube.receiveShadow = true;
        cube.userData.blockId = block.id;
        cube.name = block.label;
        cubes.push(cube);
        scene.add(cube);
      });
      mount.dataset.boardReady = "true";
      callbacksRef.current.onReady?.();
    };
    build();

    return () => {
      disposed = true;
      renderer.setAnimationLoop(null);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      renderer.domElement.removeEventListener("mousedown", handleMouseDown);
      renderer.domElement.removeEventListener("contextmenu", handleContextMenu);
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerleave", handlePointerLeave);
      renderer.domElement.removeEventListener("touchstart", handleTouchStart);
      renderer.domElement.removeEventListener("touchmove", handleTouchMove);
      window.clearTimeout(autoRotateTimer);
      controls.removeEventListener("start", handleControlsStart);
      controls.removeEventListener("end", handleControlsEnd);
      controls.dispose();
      ownedTextures.forEach((texture) => texture.dispose());
      ownedMaterials.forEach((material) => material.dispose());
      ownedGeometries.forEach((geometry) => geometry.dispose());
      boardRoot?.traverse((child) => child.isMesh && child.geometry.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      delete mount.dataset.boardReady;
    };
  }, [blocksKey]);

  return <div ref={mountRef} className={`xima-lobby-board3d ${className}`.trim()} />;
};

export default LobbyBoard3D;
